import React, { useState } from 'react';
import { X, Download, Calendar, FileText, Loader2 } from 'lucide-react';
import apiService from '../services/api';

function Report1099Modal({ technician, onClose }) {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear - 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const technicianName = technician?.person
    ? `${technician.person.first_name || ''} ${technician.person.last_name || ''}`.trim()
    : 'Unknown';

  const handlePreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.get1099Report(technician.id, selectedYear);
      setPreviewData(data);
    } catch (err) {
      setError(err.message || 'Failed to load report preview');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    setError(null);
    try {
      const blob = await apiService.download1099ReportCSV(technician.id, selectedYear);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `1099_report_${technicianName.replace(/\s+/g, '_')}_${selectedYear}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      onClose();
    } catch (err) {
      setError(err.message || 'Failed to download report');
    } finally {
      setLoading(false);
    }
  };

  const handleLastYear = () => {
    setSelectedYear(currentYear - 1);
    setPreviewData(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2">
      <div className="bg-white border border-gray-200 shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-red-800 text-white px-3 py-2 flex items-center justify-between sticky top-0 z-10 border-b border-red-900">
          <div className="flex items-center">
            <FileText className="w-4 h-4 mr-2" />
            <h2 className="text-sm font-semibold">1099 Report Download</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-3 py-3 space-y-3">
          {/* Technician Info */}
          <div className="border border-gray-200 p-2 bg-gray-50">
            <p className="text-xs text-gray-500 mb-1">Team Member</p>
            <p className="text-sm font-medium text-gray-900">{technicianName}</p>
          </div>

          {/* Year Selection */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Calendar className="w-3 h-3 inline mr-1" />
              Tax Year
            </label>
            <div className="flex gap-2">
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value));
                  setPreviewData(null);
                }}
                className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <button
                onClick={handleLastYear}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200 transition-colors"
              >
                Last Year
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 p-2 text-red-800 text-xs rounded">
              {error}
            </div>
          )}

          {/* Preview Button */}
          <button
            onClick={handlePreview}
            disabled={loading}
            className="w-full flex items-center justify-center px-3 py-2 text-xs bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <FileText className="w-3.5 h-3.5 mr-1" />
            )}
            Preview Report
          </button>

          {/* Preview Data */}
          {previewData && (
            <div className="border border-gray-200 rounded">
              <div className="bg-gray-50 px-2 py-1.5 border-b border-gray-200">
                <p className="text-xs font-medium text-gray-900">Report Summary</p>
              </div>
              <div className="p-2 space-y-2">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-gray-500">Total Appointments</p>
                    <p className="font-medium">{previewData.summary.total_appointments}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Service Amount</p>
                    <p className="font-medium">{formatCurrency(previewData.summary.total_service_amount)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Tips</p>
                    <p className="font-medium">{formatCurrency(previewData.summary.total_tips)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Payments</p>
                    <p className="font-semibold text-red-800">{formatCurrency(previewData.summary.total_amount)}</p>
                  </div>
                </div>

                {previewData.items.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">
                      Showing first 5 of {previewData.items.length} entries:
                    </p>
                    <div className="max-h-32 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-gray-500">
                            <th className="text-left py-0.5">Date</th>
                            <th className="text-left py-0.5">Client</th>
                            <th className="text-right py-0.5">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.items.slice(0, 5).map((item, index) => (
                            <tr key={index} className="border-t border-gray-100">
                              <td className="py-0.5">{item.appointment_date || 'N/A'}</td>
                              <td className="py-0.5">{item.client_name}</td>
                              <td className="py-0.5 text-right">{formatCurrency(item.total_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {previewData.items.length === 0 && (
                  <div className="text-xs text-gray-500 text-center py-2">
                    No payments found for {selectedYear}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="bg-gray-50 px-3 py-2 flex justify-end gap-2 border-t border-gray-200 sticky bottom-0">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={loading}
            className="flex items-center px-3 py-1.5 text-xs bg-red-800 text-white border border-red-900 rounded hover:bg-red-900 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5 mr-1" />
            )}
            Download CSV
          </button>
        </div>
      </div>
    </div>
  );
}

export default Report1099Modal;
